/*
  FairTools (2026-09-29): faster "Add to cart" -> cart drawer.

  Why: after /cart/add.js the theme asked for /cart.js and then /cart/?view=ajax before the drawer
  showed anything. At the same moment several apps (Pareto limits, EA Upsell popups, Upsell popup
  by Jumi, Restock Rocket, Hextom shipping bar) fire their own /cart.js reads -- about 12-17
  requests -- and Shopify answers requests for one cart one at a time, so the theme's two follow-up
  requests waited in that queue. Measured on the product page: drawer content at ~2.8-3.0s after
  the click (the same on the live theme).

  What this does: the add request itself asks Shopify to render the drawer markup
  (sections=ft-cart-drawer, see sections/ft-cart-drawer.liquid), so the drawer HTML and the item
  count come back in the add.js response. The theme's own showDrawer/updateView then run exactly
  as before; the one request updateView would make (/cart/?view=ajax) is answered from that
  response instead of the network. Everything else -- button states, error message, apps that
  watch /cart/add.js -- is unchanged, because the request is the same FormData POST the theme sends.

  Scope: only the product-page flow with the drawer cart ("go to active cart"). Quick view, gift
  card recipient forms, other cart modes and any unexpected response fall back to the theme's
  original code. Loaded for the NZ market only (layout/theme.liquid).
  To revert: remove the <script> line for this file in layout/theme.liquid.
*/
(function () {
  'use strict';

  function install() {
    var ajaxCart = window.Shopify && Shopify.theme && Shopify.theme.ajaxCart;
    if (!ajaxCart || typeof ajaxCart.addToCart !== 'function' || !window.fetch || !window.FormData) return false;
    if (ajaxCart.__ftFastCart) return true;

    var SECTION = 'ft-cart-drawer';
    var originalAddToCart = ajaxCart.addToCart;

    function hasGiftCardRecipient(form) {
      return Array.prototype.some.call(form.elements, function (el) {
        return el.name === 'properties[__shopify_send_gift_card_to_recipient]' && el.checked;
      });
    }

    // The theme's updateView reads nothing from the cart but item_count, which the drawer markup
    // already carries in <template data-options> -- so no /cart.js request is needed.
    function parseSection(html) {
      var box = document.createElement('div');
      box.innerHTML = html;
      var options = box.querySelector('[data-options]');
      if (!options || !box.querySelector('[data-html]')) return null;
      try {
        var count = parseInt(JSON.parse(options.innerHTML).item_count, 10);
        if (isNaN(count)) return null;
        return { cart: { item_count: count }, html: html };
      } catch (e) {
        return null;
      }
    }

    // Run the theme's updateView, answering its one /cart/?view=ajax fetch with markup we already have.
    // The shim is installed and removed synchronously around the call, so nothing else sees it.
    // If window.fetch cannot be replaced (a script made it read-only), updateView just fetches as usual.
    function updateViewWith(config, cart, html) {
      var target = config.cart_url + '/?view=ajax';
      var realFetch = window.fetch;
      var used = false;
      var shim = function (url) {
        if (!used && url === target) {
          used = true;
          return Promise.resolve(new Response(html, { status: 200, headers: { 'Content-Type': 'text/html' } }));
        }
        return realFetch.apply(this, arguments);
      };
      try { window.fetch = shim; } catch (e) { /* read-only: fall through */ }
      if (window.fetch !== shim) {
        ajaxCart.updateView(config, cart);
        return;
      }
      try {
        ajaxCart.updateView(config, cart);
      } finally {
        try { window.fetch = realFetch; } catch (e) { /* nothing more to do */ }
      }
    }

    // Set once a fast add has filled the drawer. The complementary-products block calls
    // ajaxCart.init() when its recommendations arrive, and init's initializeAjaxCart swaps the drawer
    // markup without binding the +/-, remove and note controls (the theme never needed to: its own
    // refresh always answered before the slow add flow did). After a fast add that swap can land
    // second, so the override below is the theme's function plus a cartEvents call in that case.
    var fastShown = false;
    ajaxCart.initializeAjaxCart = function (config) {
      fetch(config.cart_url + '/?view=ajax')
        .then(function (res) { return res.text(); })
        .then(function (response) {
          var html = Shopify.theme.ajaxCart.getAjaxCart(response);
          document.querySelectorAll('.js-ajax-cart-content').forEach(function (item) { item.innerHTML = html.content; });
          if (fastShown) Shopify.theme.ajaxCart.cartEvents(config);
        })
        .then(function () {
          if (config.show_calculator) setTimeout(function () { Shopify.theme.shippingCalculator.init(); }, 1000);
        })
        .catch(function (error) { console.log(error); });
    };

    function setButton(button, value, busy) {
      if (!button) return;
      button.value = value;
      button.classList.toggle('disabled', !!busy);
      if (busy) button.setAttribute('disabled', 'disabled');
      else button.removeAttribute('disabled');
    }

    ajaxCart.addToCart = function (form, formContext, config, isQuickview) {
      var fast = !isQuickview &&
        form instanceof HTMLFormElement &&
        formContext &&
        config && config.cart_action === 'drawer' && config.cart_added_event === 'go_to_active_cart' &&
        !hasGiftCardRecipient(form);
      if (!fast) return originalAddToCart.apply(this, arguments);

      if (window.theme && theme.ProductProperties && theme.ProductProperties.checkLineItemProperties(form) === false) return;

      var button = formContext.querySelector('.js-ajax-submit');
      setButton(button, config.adding_to_cart, true);

      var body = new FormData(form);
      body.append('sections', SECTION);
      body.append('sections_url', window.location.pathname);
      var added = false;

      fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/json' },
        body: body
      })
        .then(function (response) {
          if (!response.ok) throw response;
          return response.json();
        })
        .then(function (item) {
          added = true;
          setButton(button, config.added_to_cart, false);
          setTimeout(function () { if (button) button.value = config.add_to_cart; }, 3000);

          var parsed = item && item.sections && item.sections[SECTION] ? parseSection(item.sections[SECTION]) : null;
          if (parsed) {
            try {
              ajaxCart.showDrawer(config);
              updateViewWith(config, parsed.cart, parsed.html);
              fastShown = true;
              return;
            } catch (e) {
              console.log(e); // fall back to the theme's own refresh below
            }
          }
          // Section missing (renamed/removed) or the fast update failed: do what the theme did before.
          return Shopify.theme.cart.getCart().then(function (cart) {
            ajaxCart.showDrawer(config);
            ajaxCart.updateView(config, cart);
            fastShown = true;
          });
        })
        .catch(function (error) {
          // The item is already in the cart: never put the button back to "Add to Cart" as if the add
          // had failed (a shopper would click again and add a second one).
          if (added) { console.log(error); return; }
          setButton(button, config.add_to_cart, false);
          if (error && error.status === 422) {
            var msg = formContext.querySelector('.js-error-msg');
            if (msg && window.theme && theme.Helpers) {
              theme.Helpers.fadeIn(msg);
              setTimeout(function () { theme.Helpers.fadeOut(msg); }, 3000);
            }
          } else {
            console.log(error);
          }
        });
    };
    ajaxCart.__ftFastCart = true;
    return true;
  }

  // theme.js is loaded just before this file; if its cart object is not ready yet, try again once
  // the document has been parsed. If it never appears, the theme's own add-to-cart stays in place.
  if (!install()) document.addEventListener('DOMContentLoaded', install);
})();
